import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle } from 'lucide-react';

export interface WaiverData {
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalConditions: string;
  participantSignature: string;
}

interface WaiverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  waiverText: string;
  eventTitle: string;
  onAccept: (waiverData: WaiverData) => void;
  isLoading?: boolean;
}

export const WaiverDialog = ({
  open,
  onOpenChange,
  waiverText,
  eventTitle,
  onAccept,
  isLoading = false,
}: WaiverDialogProps) => {
  const [agreed, setAgreed] = useState(false);
  const [waiverData, setWaiverData] = useState<WaiverData>({
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    participantSignature: '',
  });

  const isFormValid = agreed && 
    waiverData.emergencyContactName.trim() !== '' &&
    waiverData.emergencyContactPhone.trim() !== '' &&
    waiverData.participantSignature.trim() !== '';

  const handleAccept = () => {
    if (isFormValid) {
      onAccept(waiverData);
      setAgreed(false);
      setWaiverData({
        emergencyContactName: '',
        emergencyContactPhone: '',
        medicalConditions: '',
        participantSignature: '',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] min-h-0 flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl">Event Waiver Required</DialogTitle>
          <DialogDescription>
            Please read and accept the waiver to RSVP to: <span className="font-semibold">{eventTitle}</span>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-1">Important Legal Document</p>
                <p>This waiver contains important information about your rights and liabilities. Please read it carefully before participating.</p>
              </div>
            </div>

            <div className="rounded-md border p-4 bg-muted/30">
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                {waiverText}
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency-name">Emergency Contact Name *</Label>
                  <Input
                    id="emergency-name"
                    value={waiverData.emergencyContactName}
                    onChange={(e) => setWaiverData({ ...waiverData, emergencyContactName: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency-phone">Emergency Contact Phone *</Label>
                  <Input
                    id="emergency-phone"
                    type="tel"
                    value={waiverData.emergencyContactPhone}
                    onChange={(e) => setWaiverData({ ...waiverData, emergencyContactPhone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="medical-conditions">Medical Conditions / Allergies (Optional)</Label>
                <Textarea
                  id="medical-conditions"
                  value={waiverData.medicalConditions}
                  onChange={(e) => setWaiverData({ ...waiverData, medicalConditions: e.target.value })}
                  placeholder="List any medical conditions, allergies, or medications the event organizer should be aware of..."
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signature">Digital Signature (Type your full name) *</Label>
                <Input
                  id="signature"
                  value={waiverData.participantSignature}
                  onChange={(e) => setWaiverData({ ...waiverData, participantSignature: e.target.value })}
                  placeholder="Type your full legal name"
                  className="font-serif text-lg"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="waiver-agree"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked as boolean)}
              />
              <Label
                htmlFor="waiver-agree"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                I have read and agree to the terms of this waiver and certify that the information provided is accurate
              </Label>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAccept} disabled={!isFormValid || isLoading}>
            {isLoading ? 'Submitting...' : 'Accept & RSVP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
